import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Lock, ChevronRight } from "lucide-react";

/* ── Rank → Color mapping (user spec) ── */
const RANK_COLORS = {
  green: "#22c55e",   // 1-3
  orange: "#f97316",  // 4-10
  red: "#ef4444",     // 11-20
  gray: "#6b7280",    // 20+
};

function getRankColor(rank) {
  if (rank <= 3) return RANK_COLORS.green;
  if (rank <= 10) return RANK_COLORS.orange;
  if (rank <= 20) return RANK_COLORS.red;
  return RANK_COLORS.gray;
}

function getRankLabel(rank) {
  if (rank <= 3) return "top3";
  if (rank <= 10) return "visible";
  if (rank <= 20) return "low";
  return "notfound";
}

/* ── SVG marker for Google Maps ── */
function createRankSvgUrl(rank) {
  const color = getRankColor(rank);
  const label = rank > 20 ? "20+" : String(rank);
  const fontSize = label.length > 2 ? 9 : 12;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34">
    <circle cx="17" cy="17" r="15" fill="${color}" stroke="white" stroke-width="2.5"/>
    <text x="17" y="${label.length > 2 ? 20 : 21}" text-anchor="middle" font-size="${fontSize}" font-weight="700" fill="white" font-family="system-ui,sans-serif">${label}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const BUSINESS_PIN_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="44"><path d="M16 0C7 0 0 7 0 16c0 12 16 28 16 28s16-16 16-28C32 7 25 0 16 0z" fill="#3B5BDB"/><circle cx="16" cy="16" r="7" fill="white"/><circle cx="16" cy="16" r="3.5" fill="#3B5BDB"/></svg>'
)}`;

/* ── Clean minimal map style ── */
const MAP_STYLES = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", stylers: [{ visibility: "simplified" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#e5e7eb" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f3f4f6" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dbeafe" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f9fafb" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
];

/* ── Google Maps component with AdvancedMarkerElement ── */
function GeoMapEmbed({ points, bizLat, bizLng, bizName, isPaid, gridSize }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [error, setError] = useState(false);
  const [watermarked, setWatermarked] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const advancedRef = useRef(false); // tracks if AdvancedMarkerElement is available

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(m => {
      if (m.setMap) m.setMap(null);
      else if (m.map !== undefined) m.map = null;
    });
    markersRef.current = [];
  }, []);

  const createRankDiv = useCallback((rank) => {
    const color = getRankColor(rank);
    const label = rank > 20 ? "20+" : String(rank);
    const el = document.createElement("div");
    el.style.cssText = `width:44px;height:44px;border-radius:50%;background:${color};border:3px solid white;display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:${rank > 9 ? "11px" : "14px"};font-family:system-ui,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.35);cursor:pointer;`;
    el.textContent = label;
    return el;
  }, []);

  const updateMarkers = useCallback((gm, map, newPoints) => {
    clearMarkers();
    newPoints.forEach(p => {
      const rank = p.rank || 21;
      if (advancedRef.current && gm.maps.marker?.AdvancedMarkerElement) {
        try {
          const marker = new gm.maps.marker.AdvancedMarkerElement({
            position: { lat: p.lat, lng: p.lng },
            map,
            content: createRankDiv(rank),
            title: `Rank ${rank}`,
            zIndex: 100,
          });
          markersRef.current.push(marker);
          return;
        } catch { /* fallback below */ }
      }
      // Fallback: legacy Marker
      const marker = new gm.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        map,
        icon: { url: createRankSvgUrl(rank), scaledSize: new gm.maps.Size(44, 44), anchor: new gm.maps.Point(22, 22) },
        zIndex: 100,
      });
      markersRef.current.push(marker);
    });
  }, [clearMarkers, createRankDiv]);

  // Initial map load — only depends on map config, NOT on points
  const pointsRef = useRef(points);
  pointsRef.current = points;

  useEffect(() => {
    if (initialized || !mapRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        // Google Maps is loaded via <script> tag in index.html with callback
        // Wait for the callback to signal ready
        if (!window.google?.maps?.Map) {
          await new Promise((resolve, reject) => {
            let elapsed = 0;
            const wait = setInterval(() => {
              if (window.google?.maps?.Map) { clearInterval(wait); resolve(); }
              elapsed += 100;
              if (elapsed > 15000) { clearInterval(wait); reject(new Error("Google Maps timeout")); }
            }, 100);
          });
        }

        const gm = window.google;
        if (cancelled || !mapRef.current || !gm?.maps) { if (!cancelled) setError(true); return; }
        advancedRef.current = false; // Disabled — requires Map ID from Google Cloud Console

        const center = { lat: bizLat || 43.65, lng: bizLng || -79.38 };
        const map = new gm.maps.Map(mapRef.current, {
          center, zoom: 14, mapTypeId: "roadmap", styles: MAP_STYLES,
          disableDefaultUI: true, zoomControl: isPaid,
          gestureHandling: isPaid ? "auto" : "none",
        });
        mapInstanceRef.current = map;

        // Business pin
        if (bizLat && bizLng) {
          new gm.maps.Marker({
            position: { lat: bizLat, lng: bizLng }, map,
            icon: { url: BUSINESS_PIN_SVG, scaledSize: new gm.maps.Size(32, 44), anchor: new gm.maps.Point(16, 44) },
            title: bizName, zIndex: 1000,
          });
        }

        // Use ref for latest points to avoid stale closure
        const currentPoints = pointsRef.current;
        updateMarkers(gm, map, currentPoints);

        if (currentPoints.length > 0) {
          const bounds = new gm.maps.LatLngBounds();
          currentPoints.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
          if (bizLat && bizLng) bounds.extend({ lat: bizLat, lng: bizLng });
          map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
        }

        // Error detection is handled in a separate useEffect below

        setInitialized(true);
      } catch (e) {
        console.error("[GeoGrid] Map load failed:", e); // eslint-disable-line no-console
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizLat, bizLng, bizName, isPaid, gridSize]);

  // Separate effect: detect Google Maps error dialogs after map initialization
  useEffect(() => {
    if (!initialized || watermarked || error) return;
    const startTimer = setTimeout(() => {
      let checks = 0;
      const errInterval = setInterval(() => {
        if (checks++ > 5) { clearInterval(errInterval); return; }
        if (!mapRef.current) return;
        const mapText = mapRef.current.innerText || "";
        if (mapText.includes("can't load Google Maps correctly") || mapText.includes("development purposes only")) {
          clearInterval(errInterval);
          setWatermarked(true);
        }
      }, 1000);
    }, 5000);
    return () => clearTimeout(startTimer);
  }, [initialized, watermarked, error]);


  // Update markers when points change (keyword tab switch)
  useEffect(() => {
    if (!initialized || !mapInstanceRef.current || !window.google) return;
    clearMarkers();
    const timer = setTimeout(() => {
      updateMarkers(window.google, mapInstanceRef.current, points);
    }, 150);
    return () => clearTimeout(timer);
  }, [points, initialized, updateMarkers, clearMarkers]);

  if (error || watermarked) {
    return <DotGridFallback points={points} gridSize={gridSize} showActivating={watermarked} />;
  }
  return <div ref={mapRef} data-testid="geo-grid-google-map" style={{ width: "100%", height: "100%", borderRadius: 12 }} />;
}

/* ── CSS dot grid fallback ── */
function DotGridFallback({ points, gridSize, showActivating = false }) {
  const gap = gridSize === 3 ? 24 : 16;
  const dotSize = gridSize === 3 ? 48 : 38;
  return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, #f0f4f0 0%, #f4f2f0 25%, #f0f2f4 50%, #f4f0f0 75%, #f0f4f2 100%)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
      {/* Fake road lines */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: 12, opacity: 0.3 }}>
        <div style={{ position: "absolute", top: "25%", left: 0, right: 0, height: 2, background: "#d1d5db" }} />
        <div style={{ position: "absolute", top: "55%", left: 0, right: 0, height: 3, background: "#d1d5db" }} />
        <div style={{ position: "absolute", top: "80%", left: 0, right: 0, height: 2, background: "#d1d5db", transform: "rotate(3deg)" }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, left: "35%", width: 2, background: "#d1d5db" }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, left: "68%", width: 3, background: "#d1d5db" }} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${gridSize}, ${dotSize}px)`, gap, position: "relative", zIndex: 2 }}>
        {points.map((pt, i) => {
          const rk = pt.rank || 21;
          const bg = getRankColor(rk);
          return (
            <div key={i} style={{ width: dotSize, height: dotSize, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: rk > 20 ? 10 : 13, border: "2.5px solid #fff", boxShadow: "0 2px 8px rgba(0,0,0,.2)", transition: "transform .2s" }}>
              {rk > 20 ? "20+" : rk}
            </div>
          );
        })}
      </div>
      {showActivating && (
        <div style={{ position: "absolute", top: 10, left: 12, display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.9)", backdropFilter: "blur(8px)", padding: "5px 12px", borderRadius: 6, border: "1px solid #e5e7eb" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#f59e0b", animation: "pulse 1.5s infinite" }} />
          <span style={{ fontSize: 10, fontWeight: 600, color: "#92400e" }}>Map view activating...</span>
        </div>
      )}
      <div style={{ position: "absolute", bottom: 8, right: 12, fontSize: 9, color: "#9ca3af", fontFamily: "monospace" }}>
        {gridSize}x{gridSize} grid &middot; 1.5km spacing
      </div>
    </div>
  );
}

/* ── Animated number (count-up) ── */
function AnimatedNumber({ value, duration = 300, prefix = "", suffix = "" }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    prevRef.current = value;
    if (from === to) return;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [value, duration]);

  return <>{prefix}{display}{suffix}</>;
}

/* ── Stats Panel (left side) ── */
function StatsPanel({ data, keyword }) {
  const pts = data?.points || [];
  const total = pts.length;
  const top3 = pts.filter(p => (p.rank || 21) <= 3).length;
  const top10 = pts.filter(p => (p.rank || 21) <= 10).length;
  const rank11plus = pts.filter(p => { const r = p.rank || 21; return r > 10 && r <= 20; }).length;
  const rank20plus = pts.filter(p => (p.rank || 21) > 20).length;
  const avgRank = data?.average_rank || (total > 0 ? Math.round(pts.reduce((s, p) => s + (p.rank || 21), 0) / total * 10) / 10 : 20);
  const visRate = data?.visibility_rate ?? (total > 0 ? Math.round(top10 / total * 100 * 10) / 10 : 0);

  const rankColor = avgRank <= 3 ? RANK_COLORS.green : avgRank <= 10 ? RANK_COLORS.orange : RANK_COLORS.red;

  return (
    <div data-testid="geo-grid-stats-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Active keyword */}
      <div style={{ padding: "10px 14px", background: "#f0f4ff", borderRadius: 10, border: "1px solid #dbeafe" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>"{keyword}"</div>
      </div>

      {/* Rank + Visibility */}
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, padding: "12px 10px", background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", textAlign: "center" }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", marginBottom: 4 }}>Avg Rank</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: rankColor }}>#<AnimatedNumber value={Math.round(avgRank)} /></div>
        </div>
        <div style={{ flex: 1, padding: "12px 10px", background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", textAlign: "center" }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", marginBottom: 4 }}>Visibility</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: visRate >= 60 ? RANK_COLORS.green : visRate > 30 ? RANK_COLORS.orange : RANK_COLORS.red }}><AnimatedNumber value={Math.round(visRate)} suffix="%" /></div>
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Found in {top10} of {total} points</span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
          <div style={{ height: "100%", borderRadius: 3, background: visRate >= 60 ? RANK_COLORS.green : visRate > 30 ? RANK_COLORS.orange : RANK_COLORS.red, width: `${visRate}%`, transition: "width 0.5s ease" }} />
        </div>
      </div>

      {/* Counts */}
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1, textAlign: "center", padding: "8px 4px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: RANK_COLORS.green }}><AnimatedNumber value={top3} /></div>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#166534" }}>Top 3</div>
        </div>
        <div style={{ flex: 1, textAlign: "center", padding: "8px 4px", background: "#fff7ed", borderRadius: 8, border: "1px solid #fed7aa" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: RANK_COLORS.orange }}><AnimatedNumber value={top10} /></div>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#9a3412" }}>Top 10</div>
        </div>
      </div>

      {/* Breakdown badges */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        {[
          { label: "1-3", count: top3, bg: RANK_COLORS.green },
          { label: "4-10", count: top10 - top3, bg: RANK_COLORS.orange },
          { label: "11-20", count: rank11plus, bg: RANK_COLORS.red },
          { label: "20+", count: rank20plus, bg: RANK_COLORS.gray },
        ].map(b => (
          <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "#f9fafb", borderRadius: 6, border: "1px solid #e5e7eb" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: b.bg, flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>Rank {b.label}:</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#111827", marginLeft: "auto" }}>{b.count}</span>
          </div>
        ))}
      </div>

      {/* Data source */}
      <div style={{ fontSize: 10, color: "#9ca3af", textAlign: "center" }}>
        {data?.source === "live" ? "Live SERP data" : "Estimated data"} &middot; 1.5km grid
      </div>
    </div>
  );
}

/* ── Free audit blur overlay (outer ring frosted, center visible) ── */
function FreeBlurOverlay({ onUnlock }) {
  return (
    <div data-testid="geo-grid-blur-overlay" style={{ position: "absolute", inset: 0, zIndex: 10, borderRadius: 12, overflow: "hidden" }}>
      {/* Frosted glass ring: full blur, with center cut out */}
      <div style={{ position: "absolute", inset: 0, display: "flex" }}>
        {/* Top strip */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "22%", background: "rgba(255,255,255,0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} />
        {/* Bottom strip */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "22%", background: "rgba(255,255,255,0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} />
        {/* Left strip (middle height) */}
        <div style={{ position: "absolute", top: "22%", left: 0, width: "22%", height: "56%", background: "rgba(255,255,255,0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} />
        {/* Right strip (middle height) */}
        <div style={{ position: "absolute", top: "22%", right: 0, width: "22%", height: "56%", background: "rgba(255,255,255,0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} />
      </div>
      {/* Lock CTA */}
      <div style={{
        position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
        background: "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)",
        borderRadius: 12, padding: "12px 24px", textAlign: "center",
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)", border: "1px solid rgba(0,0,0,0.06)",
        cursor: "pointer", maxWidth: 280, zIndex: 20, pointerEvents: "auto",
      }} onClick={onUnlock}>
        <Lock size={16} style={{ margin: "0 auto 4px", color: "#dc2626" }} />
        <div style={{ fontSize: 12, fontWeight: 800, color: "#111827" }}>Unlock all 25 ranking points</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Full 5x5 geo-grid — $27</div>
      </div>
    </div>
  );
}

/* ═══ Main GeoGridSection ═══ */
export default function GeoGridSection({ geo_grid, business, hasAccess, onUnlock }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const geoData = geo_grid || {};

  // Separate meta from keyword data
  const meta = geoData.__meta__ || {};
  const keywords = Object.keys(geoData).filter(k => k !== "__meta__");

  if (keywords.length === 0) return null;

  const activeKw = keywords[activeIdx] || keywords[0];
  const raw = geoData[activeKw];
  const data = raw || {};
  // Handle multiple data formats: array of points, object with .points, or nested structure
  let pts;
  if (Array.isArray(raw)) {
    pts = raw;
  } else if (raw?.points && Array.isArray(raw.points)) {
    pts = raw.points;
  } else {
    pts = [];
  }

  const gridSize = meta.grid_size || (pts.length <= 9 ? 3 : 5);
  const bizLat = business?.location?.lat || business?.geometry?.location?.lat || business?.lat;
  const bizLng = business?.location?.lng || business?.geometry?.location?.lng || business?.lng;

  // For free users, expand 3x3 data to 5x5 visual with blurred outer ring
  const displayGridSize = hasAccess ? gridSize : 5;
  const displayPoints = pts;

  return (
    <section id="geogrid" data-testid="geo-grid-section" style={{ marginBottom: 40 }}>
      <style>{`
        .geo-stats-panel { width: 280px; flex-shrink: 0; }
        .geo-map-container { min-width: 300px; }
        .geo-kw-tabs { display: flex; gap: 0; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        @media (max-width: 768px) {
          .geo-stats-panel { width: 100% !important; flex-shrink: 1; }
          .geo-map-container { min-width: 100% !important; min-height: 300px !important; }
          .geo-kw-tabs button { padding: 8px 14px !important; font-size: 12px !important; white-space: nowrap; }
        }
      `}</style>
      {/* Section header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", fontFamily: "'JetBrains Mono',monospace", letterSpacing: ".08em" }}>03</span>
          <div style={{ height: 1, flex: 1, background: "#E2E8F0" }} />
          <span style={{ fontSize: 10, fontWeight: 600, padding: "3px 8px", background: hasAccess ? "#ECFDF5" : "#FFFBEB", color: hasAccess ? "#059669" : "#D97706", textTransform: "uppercase", letterSpacing: "0.05em", border: `1px solid ${hasAccess ? "#A7F3D0" : "#FDE68A"}`, fontFamily: "'JetBrains Mono',monospace" }}>
            {hasAccess ? "LIVE" : "EST"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <h2 data-testid="geo-grid-title" style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", margin: 0, fontFamily: "'Outfit',sans-serif" }}>
            Where You Rank
          </h2>
        </div>
        <p style={{ fontSize: 13, color: "#64748B", margin: 0, lineHeight: 1.5, fontFamily: "'IBM Plex Sans',sans-serif" }}>
          Your Google Maps ranking at {displayGridSize * displayGridSize} zones around your location ({displayGridSize}x{displayGridSize} grid, 1.5km spacing).
          <span style={{ color: "#E11D48", fontWeight: 600 }}> Red = invisible to customers.</span>
        </p>
      </div>

      {/* Keyword tabs — underline style */}
      <div data-testid="geo-grid-keyword-tabs" className="geo-kw-tabs" style={{ marginBottom: 16, borderBottom: "2px solid #e5e7eb" }}>
        {keywords.map((kw, i) => (
          <button
            key={kw}
            data-testid={`geo-grid-keyword-tab-${i}`}
            onClick={() => setActiveIdx(i)}
            style={{
              padding: "10px 20px", fontSize: 13, cursor: "pointer",
              fontWeight: activeIdx === i ? 700 : 400,
              color: activeIdx === i ? "#3b5bdb" : "#6b7280",
              background: "transparent", border: "none", borderBottom: activeIdx === i ? "2.5px solid #3b5bdb" : "2.5px solid transparent",
              marginBottom: -2, transition: "all 0.2s ease",
            }}
          >
            {kw}
          </button>
        ))}
      </div>

      {/* Main layout: stats panel + map — stacks on mobile */}
      <div style={{ display: "flex", gap: 20, alignItems: "stretch", flexWrap: "wrap" }} data-testid="geo-grid-layout">
        {/* Left stats panel — full width on mobile */}
        <div className="geo-stats-panel">
          <StatsPanel data={data} keyword={activeKw} />
        </div>

        {/* Right map — full width on mobile */}
        <div className="geo-map-container" style={{ flex: 1, minHeight: 400, position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb", background: "#f9fafb" }}>
          <GeoMapEmbed
            points={displayPoints}
            bizLat={bizLat}
            bizLng={bizLng}
            bizName={business?.name || ""}
            isPaid={hasAccess}
            gridSize={gridSize}
          />
          {/* Free users: frosted glass overlay on outer ring */}
          {!hasAccess && <FreeBlurOverlay onUnlock={onUnlock} />}
        </div>
      </div>

      {/* Color legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12, justifyContent: "center" }}>
        {[
          { color: RANK_COLORS.green, label: "Rank 1-3" },
          { color: RANK_COLORS.orange, label: "Rank 4-10" },
          { color: RANK_COLORS.red, label: "Rank 11-20" },
          { color: RANK_COLORS.gray, label: "Not visible (20+)" },
        ].map(l => (
          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#6b7280" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: l.color }} />
            {l.label}
          </div>
        ))}
      </div>

      {/* Urgency CTA for free users */}
      {!hasAccess && (() => {
        const invisible = pts.filter(p => (p.rank || 21) > 10).length;
        return (
          <div data-testid="geo-grid-unlock-cta" style={{ marginTop: 16, padding: "14px 20px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={onUnlock}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>
                {invisible > 0
                  ? `${invisible} search areas where customers can't find you`
                  : "See your full 25-point coverage map"}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                {invisible > 0
                  ? "Unlock the full 5x5 geo-grid to see all blind spots"
                  : "Upgrade to a 5x5 grid with 25 ranking points — $27"}
              </div>
            </div>
            <ChevronRight size={18} style={{ color: "#dc2626", flexShrink: 0 }} />
          </div>
        );
      })()}
    </section>
  );
}
